import { Injectable } from '@angular/core';

/**
 * When a Super Admin "proxies" an organization, we store the target org id in
 * sessionStorage and send it on every API request as X-Proxy-Organization-Id.
 * The server then treats the session as that organization (JWT type 2) for that request.
 *
 * Only JWT payload `type === 1` is honored by the API for proxy (see verifyToken.js).
 */
@Injectable({
  providedIn: 'root',
})
export class SuperAdminProxyService {
  private readonly ORG_ID_KEY = 'superAdminProxyOrgId';
  private readonly ORG_NAME_KEY = 'superAdminProxyOrgName';

  /** Decode access token payload (Angular user session stores token under user.token). */
  getJwtPayload(): Record<string, unknown> | null {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) {
        return null;
      }
      const p = JSON.parse(raw) as { token?: string; user?: { token?: string } };
      const t =
        typeof p?.token === 'string'
          ? p.token
          : typeof p?.user?.token === 'string'
            ? p.user.token
            : null;
      if (!t) {
        return null;
      }
      const parts = t.split('.');
      if (parts.length < 2) {
        return null;
      }
      let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) {
        base64 += '=';
      }
      const json = atob(base64);
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  /** Server only applies X-Proxy-Organization-Id when JWT type is 1 (Super Admin). */
  canUseOrgProxy(): boolean {
    const payload = this.getJwtPayload();
    return payload != null && Number(payload['type']) === 1;
  }

  setProxy(orgId: number, orgName: string): void {
    sessionStorage.setItem(this.ORG_ID_KEY, String(orgId));
    sessionStorage.setItem(this.ORG_NAME_KEY, orgName || '');
  }

  clearProxy(): void {
    sessionStorage.removeItem(this.ORG_ID_KEY);
    sessionStorage.removeItem(this.ORG_NAME_KEY);
  }

  getProxyOrgId(): number | null {
    const v = sessionStorage.getItem(this.ORG_ID_KEY);
    if (v == null || v === '') {
      return null;
    }
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  }

  getProxyOrgName(): string {
    return sessionStorage.getItem(this.ORG_NAME_KEY) || '';
  }

  isProxying(): boolean {
    return this.getProxyOrgId() != null;
  }
}
